import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MarketingNav, Footer } from "@/components/Marketing";
import { SlashMark } from "@/components/Logo";
import { api, mediaUrl } from "@/lib/api";
import { themeOf } from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Layers, Eye } from "lucide-react";

export default function CollectionPublic() {
  const { username, slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/collections/public/${username}/${slug}`); setC(data); }
      catch { setErr(true); }
    })();
  }, [username, slug]);

  if (err) return (
    <div className="bg-background min-h-screen"><MarketingNav />
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <SlashMark size={40} color="hsl(var(--primary))" className="mx-auto mb-4" />
        <h1 className="font-display text-2xl font-semibold text-foreground">Collection not found</h1>
        <Link to="/discover"><Button className="mt-6 rounded-full">Explore timelines</Button></Link>
      </div>
    </div>
  );
  if (!c) return <div className="min-h-screen grid place-items-center bg-background"><div className="animate-pulse"><SlashMark size={40} color="hsl(var(--primary))" /></div></div>;

  return (
    <div className="bg-background min-h-screen">
      <MarketingNav />
      <div className="relative grain border-b border-border">
        <div className="absolute inset-0 opacity-[0.1]" style={{ background: "radial-gradient(700px 320px at 15% -20%, #3A1F6E, transparent)" }} aria-hidden />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 relative">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-primary mb-4"><Layers size={14} /> Collection</div>
          <h1 className="font-display text-5xl font-semibold tracking-tight text-foreground">{c.title}</h1>
          {c.description && <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{c.description}</p>}
          <div className="mt-4 text-sm text-muted-foreground">Curated by <Link to={`/u/${c.owner_username}`} className="text-primary hover:underline">@{c.owner_username}</Link> · {c.timelines.length} timelines</div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        {c.timelines.length === 0 ? (
          <p className="text-muted-foreground">No public timelines in this collection yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.timelines.map((tl, i) => {
              const t = themeOf(tl.theme);
              return (
                <motion.div key={tl.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                  <Link to={`/t/${tl.creator_username}/${tl.slug}`} data-testid={`collection-tl-${tl.slug}`}
                    className="group block rounded-2xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1">
                    <div className="h-40 relative overflow-hidden" style={{ background: t.bgGradient }}>
                      {tl.cover_image && <img src={mediaUrl(tl.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-85 transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
                      <span className="absolute top-3 left-3 text-[11px] px-2 py-0.5 rounded-full glass font-medium" style={{ color: t.text }}>{tl.category}</span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">{tl.title}</h3>
                      <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{tl.description}</p>
                      <div className="mt-3 flex items-center gap-1 text-xs text-muted-foreground"><Eye size={13} /> {tl.views?.toLocaleString()} · {tl.event_count} events</div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
